import * as React from 'react';
import { AlertTriangle, Camera, Check, X } from 'lucide-react';
import { Surface } from './Card';
import { Icon } from './Icon';
import { Inline, Stack } from './Layout';
import { Text } from './Text';
import { ToggleGroup, ToggleGroupItem } from './ToggleGroup';
import type { AnswerStatus } from '../lib/inspections';

export interface ChecklistQuestionProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, 'title'> {
  /** The question, as the checklist asks it: "Lights work". */
  label: React.ReactNode;
  /** The checklist's help under it: "Headlights, brake lights, beacons". */
  help?: React.ReactNode;
  /**
   * One quiet line saying what the question lets the person skip:
   * "Optional · Photo needed if it fails".
   */
  rules?: React.ReactNode;
  /** The answer: a PassFailToggle, a number, the options to pick from. */
  children?: React.ReactNode;
  /**
   * The note and photos under the answer, in a well of their own. Left out,
   * there is no well.
   */
  detail?: React.ReactNode;
  /** The well's heading. "Document the issue" when failed. */
  detailTitle?: React.ReactNode;
  /** A failed answer: the well turns red and its glyph a warning. */
  failed?: boolean;
  /** What is still wanted, in red at the foot: "Add a photo before you send this". */
  message?: React.ReactNode;
}

/**
 * One question of a checklist being run: what it asks and the rules under
 * it, the answer, then the note and photos in a well, which turns red when
 * the answer failed so the fault reads as the thing to document.
 *
 *   <ChecklistQuestion label="Lights work" rules="Photo needed if it fails"
 *     failed={failed} detailTitle="Document the issue"
 *     detail={<>…note… …photos…</>}
 *     message={needsPhoto ? 'Photograph what is wrong before you send this' : undefined}>
 *     <PassFailToggle label="Lights work" value={status} onValueChange={setStatus} />
 *   </ChecklistQuestion>
 *
 * The phone's run screen draws every question with it. The answer is the
 * caller's, so a number, a choice or a set of ticks sits in the same card.
 */
export const ChecklistQuestion = React.forwardRef<
  HTMLDivElement,
  ChecklistQuestionProps
>(function ChecklistQuestion(
  {
    label,
    help,
    rules,
    children,
    detail,
    detailTitle,
    failed = false,
    message,
    ...props
  },
  ref
) {
  return (
    <Surface ref={ref} radius="xl" padding="md" tone="default" {...props}>
      <Stack gap={4}>
        <Stack gap={1}>
          <Text variant="body-sm-strong">{label}</Text>
          {help ? (
            <Text variant="caption" tone="muted">
              {help}
            </Text>
          ) : null}
          {rules ? (
            <Text variant="caption" tone="muted">
              {rules}
            </Text>
          ) : null}
        </Stack>
        {children}
        {detail ? (
          <Surface
            radius="lg"
            padding="sm"
            tone={failed ? 'destructive' : 'muted'}
          >
            <Stack gap={3}>
              {detailTitle ? (
                <Inline gap={2} align="center">
                  <Icon
                    icon={failed ? AlertTriangle : Camera}
                    size="xs"
                    tone={failed ? 'destructive' : 'muted'}
                  />
                  <Text
                    variant="caption-strong"
                    tone={failed ? 'destructive' : 'muted'}
                  >
                    {detailTitle}
                  </Text>
                </Inline>
              ) : null}
              {detail}
              {message ? (
                <Text variant="caption-strong" tone="destructive">
                  {message}
                </Text>
              ) : null}
            </Stack>
          </Surface>
        ) : null}
      </Stack>
    </Surface>
  );
});

export interface PassFailToggleProps {
  /** The question it answers, for a screen reader: "Lights work". */
  label: string;
  value?: AnswerStatus;
  onValueChange: (value: AnswerStatus) => void;
}

/**
 * Pass or Fail, as two options side by side: the chosen one takes the
 * subtle fill of its meaning, green or red, and the other stays an outline,
 * so the pair reads as one control. A radio group to a screen reader, and
 * the arrow keys move between the two.
 *
 *   <PassFailToggle label="Lights work" value={status} onValueChange={setStatus} />
 *
 * The phone's run screen and the office's run page both draw it, so a check
 * reads the same whichever screen it was filled in on.
 */
export const PassFailToggle = React.forwardRef<
  HTMLDivElement,
  PassFailToggleProps
>(function PassFailToggle({ label, value, onValueChange }, ref) {
  return (
    <ToggleGroup
      ref={ref}
      type="single"
      value={value ?? ''}
      // Pressing the chosen answer again would clear it; an answer, once
      // given, is changed by choosing the other one.
      onValueChange={(next) => next && onValueChange(next as AnswerStatus)}
      role="radiogroup"
      aria-label={`${label}: pass or fail`}
      variant="outline"
      size="lg"
      gap={2}
      bordered={false}
      fullWidth
    >
      <ToggleGroupItem value="PASS" tone="success">
        <Check />
        Pass
      </ToggleGroupItem>
      <ToggleGroupItem value="FAIL" tone="destructive">
        <X />
        Fail
      </ToggleGroupItem>
    </ToggleGroup>
  );
});
